import React from "react";
import { Truck, Clock, MapPin, RefreshCw } from "lucide-react";

const fmtTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw.replace ? raw.replace(" ", "T") : raw);
  if (isNaN(d)) return raw;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
};

const VehicleSidebar = ({
  vehicles,           // tracked vehicles list from useTrackedVehicles()
  vehicleDataMap,     // { vehicleRegNo: txns[] } from useAllFastagFromDB()
  selectedVehicleNo,  // currently selected vehicle reg number
  onVehicleSelect,    // (vehicleRegNo) => void
  isFetching,
}) => {
  return (
    <div className="w-64 flex-shrink-0 bg-white border-r border-slate-200 flex flex-col overflow-hidden">

      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Vehicles
        </span>
        <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
          {vehicles.length}
        </span>
      </div>

      {/* List */}
      <div className="overflow-y-auto flex-1">
        {vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-300 py-12">
            <Truck size={28} strokeWidth={1.5} />
            <p className="text-xs text-slate-400">No vehicles found</p>
          </div>
        ) : (
          vehicles.map((vehicle) => {
            const txns       = vehicleDataMap[vehicle.vehicleRegNo] || [];
            const lastTxn    = txns[0];
            const isSelected = selectedVehicleNo === vehicle.vehicleRegNo;
            const states     = [...new Set(txns.map((t) => t.tollState).filter(Boolean))];
            const plazaCount = new Set(txns.map((t) => t.tollPlazaName)).size;

            return (
              <button
                key={vehicle.vehicleRegNo}
                onClick={() => onVehicleSelect(vehicle.vehicleRegNo)}
                className={`w-full text-left flex flex-col gap-1.5 px-4 py-3 border-b border-slate-100 transition-colors border-l-[3px]
                  ${isSelected
                    ? "bg-amber-50 border-l-amber-400"
                    : "hover:bg-slate-50 border-l-transparent"
                  }`}
              >
                {/* Reg number + type */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected ? "bg-amber-400" : "bg-slate-900"
                    }`}>
                      <Truck size={12} className={isSelected ? "text-slate-900" : "text-white"} />
                    </div>
                    <span className={`font-mono text-xs font-bold tracking-wider truncate ${
                      isSelected ? "text-amber-700" : "text-slate-800"
                    }`}>
                      {vehicle.vehicleRegNo}
                    </span>
                  </div>

                  {/* Fetching spinner or txn count */}
                  {isFetching ? (
                    <RefreshCw size={10} className="text-amber-400 animate-spin flex-shrink-0" />
                  ) : txns.length > 0 ? (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      isSelected
                        ? "bg-amber-200 text-amber-800"
                        : "bg-slate-100 text-slate-500"
                    }`}>
                      {txns.length}
                    </span>
                  ) : null}
                </div>

                {/* Vehicle type + plaza count */}
                <div className="flex items-center gap-2 pl-8">
                  <span className="text-[10px] text-slate-400">
                    {vehicle.vehicleType !== "—" ? vehicle.vehicleType : "Unknown type"}
                  </span>
                  {plazaCount > 0 && (
                    <>
                      <span className="text-slate-200">·</span>
                      <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                        <MapPin size={8} />
                        {plazaCount} plaza{plazaCount !== 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </div>

                {/* State tags */}
                {states.length > 0 && (
                  <div className="flex flex-wrap gap-1 pl-8">
                    {states.slice(0, 2).map((s) => (
                      <span
                        key={s}
                        className="text-[9px] font-medium bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full"
                      >
                        {s}
                      </span>
                    ))}
                    {states.length > 2 && (
                      <span className="text-[9px] font-medium bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded-full">
                        +{states.length - 2}
                      </span>
                    )}
                  </div>
                )}

                {/* Last toll time */}
                {lastTxn && (
                  <div className="flex items-center gap-1 pl-8 text-[10px] text-slate-400">
                    <Clock size={8} />
                    <span>{fmtTime(lastTxn.readerReadTime)}</span>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VehicleSidebar;